import { INominalCodeMapping, INominalSuggestion, INominalSuggestionPayload, INominalSummaryRow, IPostNominalCodeMapping } from '../services/nominal-code-mapping/nominal-code-mapping.model'
import { ISpendBasedConversion } from '../services/spend-based-conversion/spend-based-conversion.model'
import type { INominalOutcomeValue } from '../../components/presentation-components/settings-components/data-import/nominal-mapping-outcome-picker'

// Helpers shared by the Chart of Accounts page, the import step's inline table and the table they
// both render: one key for a code, one way to read a saved mapping or a suggestion as an outcome,
// one way to record an edit, and one payload builder for the bulk save.

/** The key a code is matched on everywhere, front and back: trimmed and lower-cased. */
export const normaliseNominalCode = (code?: string | null): string => String(code ?? '').trim().toLowerCase()

/**
 * An unsaved change to one code. Held by the page and keyed by the normalised code. It carries its
 * own code and description so it can still be saved if the row drops out of the loaded summary.
 */
export interface INominalRowEdit {
  nominalCode: string
  nominalDescription: string | null
  // The outcome being set. Null when a saved mapping has been cleared: it is deleted on save.
  value: INominalOutcomeValue | null
  // The saved mapping this change replaces or clears, if there is one.
  mappingId: string | null
  // The activity behind value.spendBasedConversionId, so the row can show it even when it is not
  // in the default source's list (an activity picked in the Advanced drawer from another source).
  conversion?: ISpendBasedConversion | null
  // The suggestion this row was accepted from. Kept if the outcome is then changed, so the save
  // records the suggestion as overridden rather than forgetting it was ever made.
  suggestion?: INominalSuggestion | null
}

/** A saved mapping as the outcome value the pickers work with. */
export const mappingToOutcomeValue = (mapping?: INominalCodeMapping | null): INominalOutcomeValue | null => {
  if (!mapping) return null
  return {
    mappingType: mapping.mappingType,
    spendBasedConversionId: mapping.spendBasedConversionId ?? null,
    conversionSourceId: mapping.conversionSourceId ?? null,
    excludeReason: mapping.excludeReason ?? null,
    groupId: mapping.groupId ?? null,
  }
}

// For comparing activity names: case and runs of spaces do not count.
const normaliseText = (value?: string | null): string => String(value ?? '').replace(/\s+/g, ' ').trim().toLowerCase()

const blankToNull = (value?: string | null): string | null => {
  if (value == null) return null
  const trimmed = String(value).trim()
  return trimmed === '' ? null : trimmed
}

/** True when two outcomes would save the same mapping. Used to drop an edit that undoes itself. */
export const isSameOutcome = (a: INominalOutcomeValue | null, b: INominalOutcomeValue | null): boolean => {
  if (!a || !b) return a === b
  if (a.mappingType !== b.mappingType) return false
  if (a.mappingType === 'spend') {
    return blankToNull(a.spendBasedConversionId) === blankToNull(b.spendBasedConversionId)
      && blankToNull(a.conversionSourceId) === blankToNull(b.conversionSourceId)
      && blankToNull(a.groupId) === blankToNull(b.groupId)
  }
  if (a.mappingType === 'exclude') {
    return blankToNull(a.excludeReason) === blankToNull(b.excludeReason)
  }
  return true
}

/**
 * The activity a spend suggestion points at, among the activities loaded for the organisation's
 * default source. By id first, then by activity name, because factor-year resolution moves the id
 * from one year to the next while the activity stays the same.
 */
export const findSuggestedActivity = (
  suggestion: INominalSuggestion,
  activities: ISpendBasedConversion[],
): ISpendBasedConversion | null => {
  if (suggestion.mappingType !== 'spend') return null
  if (suggestion.spendBasedConversionId) {
    const byId = activities.find(activity => activity.id === suggestion.spendBasedConversionId)
    if (byId) return byId
  }
  const name = normaliseText(suggestion.activity)
  if (name) {
    const byName = activities.find(activity => normaliseText(activity.activity) === name)
    if (byName) return byName
  }
  return null
}

/** Whether a suggestion can be accepted as it stands. An exclusion or "Don't import" always can. */
export const canAcceptSuggestion = (suggestion: INominalSuggestion | null | undefined, activities: ISpendBasedConversion[]): boolean => {
  if (!suggestion) return false
  if (suggestion.mappingType === 'exclude' || suggestion.mappingType === 'private') return true
  return findSuggestedActivity(suggestion, activities) !== null
}

/** A suggestion as the outcome it would set, or null when it cannot be resolved to an activity. */
export const suggestionToOutcome = (
  suggestion: INominalSuggestion,
  activities: ISpendBasedConversion[],
): { value: INominalOutcomeValue, conversion: ISpendBasedConversion | null } | null => {
  if (suggestion.mappingType === 'private') {
    return {
      value: { mappingType: 'private', spendBasedConversionId: null, conversionSourceId: null, excludeReason: null, groupId: null },
      conversion: null,
    }
  }
  if (suggestion.mappingType === 'exclude') {
    return {
      value: {
        mappingType: 'exclude',
        spendBasedConversionId: null,
        conversionSourceId: null,
        // The reason the suggestion gave is kept, so an accepted exclusion still says why.
        excludeReason: blankToNull(suggestion.reason),
        groupId: null,
      },
      conversion: null,
    }
  }
  const activity = findSuggestedActivity(suggestion, activities)
  if (!activity) return null
  return {
    value: {
      mappingType: 'spend',
      spendBasedConversionId: activity.id,
      conversionSourceId: activity.conversionSourceId ?? null,
      excludeReason: null,
      groupId: blankToNull(suggestion.groupId),
    },
    conversion: activity,
  }
}

/** The suggestion as sent with a save: ids and enums only. */
export const toSuggestionPayload = (suggestion: INominalSuggestion): INominalSuggestionPayload => ({
  mappingType: suggestion.mappingType,
  spendBasedConversionId: blankToNull(suggestion.spendBasedConversionId),
  defraIdentifier: blankToNull(suggestion.defraIdentifier),
  confidence: suggestion.confidence,
  signal: suggestion.signal ?? null,
})

/**
 * The bulk-save item for one edit, or null when the edit is not a complete decision (a cleared
 * mapping, which is deleted instead, or a spend outcome with no activity chosen yet). Only the
 * fields the chosen outcome uses are sent, and every id is null rather than blank.
 */
export const buildNominalMappingPayloadItem = (edit: INominalRowEdit): IPostNominalCodeMapping | null => {
  const value = edit.value
  if (!value) return null
  const isSpend = value.mappingType === 'spend'
  const isExclude = value.mappingType === 'exclude'
  const isPrivate = value.mappingType === 'private'
  if (!isSpend && !isExclude && !isPrivate) return null
  if (isSpend && !blankToNull(value.spendBasedConversionId)) return null
  return {
    nominalCode: edit.nominalCode,
    nominalDescription: blankToNull(edit.nominalDescription),
    mappingType: value.mappingType,
    spendBasedConversionId: isSpend ? blankToNull(value.spendBasedConversionId) : null,
    conversionSourceId: isSpend ? blankToNull(value.conversionSourceId) : null,
    excludeReason: isExclude ? blankToNull(value.excludeReason) : null,
    groupId: isSpend ? blankToNull(value.groupId) : null,
    ...(edit.suggestion ? { suggestion: toSuggestionPayload(edit.suggestion) } : {}),
  }
}

// A factor code ending in a region ("24.GB", as EXIOBASE codes do) marks an activity whose name
// ends in that region spelled out: "Basic iron and steel (United Kingdom)".
const REGION_CODE_PATTERN = /\.[A-Z]{2}$/
const TRAILING_BRACKET_PATTERN = /\s*\([^()]*\)\s*$/
// The "Code 24.GB: " the shared display text puts in front of an activity.
const CODE_PREFIX_PATTERN = /^Code\s+[^:]*:\s*/

/**
 * The bare activity name, for anywhere a chosen or suggested activity is shown on the Chart of
 * Accounts table: no "Code X:" in front and no region after it. Read from the conversion's
 * `activity` field when there is a conversion; the region is dropped only when the factor code
 * carries one, so a name that merely ends in brackets ("Liquefied Petroleum Gases (LPG)") keeps
 * them. Given text instead, only a leading "Code X:" is taken off. Lists of activities keep the
 * full display text, so a factor code can still be searched for.
 */
export const formatNominalActivityName = (
  input: Pick<ISpendBasedConversion, 'activity' | 'localAuthorityCustomIdentifier'> | string | null | undefined,
): string => {
  if (input == null) return ''
  if (typeof input === 'string') return input.replace(CODE_PREFIX_PATTERN, '').trim()
  const name = String(input.activity ?? '').trim()
  const code = String(input.localAuthorityCustomIdentifier ?? '').trim()
  if (!name || !REGION_CODE_PATTERN.test(code)) return name
  return name.replace(TRAILING_BRACKET_PATTERN, '').trim() || name
}

/**
 * The chip for a suggestion. Low confidence reads as a question rather than an answer. Pass the
 * activity's bare name when the suggestion resolves to a loaded activity; the suggestion's own
 * text is used otherwise.
 */
export const getSuggestionChipLabel = (suggestion: INominalSuggestion, activityName?: string | null): string => {
  if (suggestion.mappingType === 'private') {
    return suggestion.confidence === 'low' ? "Don't import?" : "Suggested: don't import"
  }
  if (suggestion.mappingType === 'exclude') {
    return suggestion.confidence === 'low' ? 'Exclude?' : 'Suggested: exclude'
  }
  const activity = activityName || formatNominalActivityName(suggestion.activity) || 'a spend activity'
  return suggestion.confidence === 'low' ? `${activity}?` : `Suggested: ${activity}`
}

/** The status filters above the table. Membership follows the saved state, not unsaved edits. */
export type NominalStatusFilter = 'needsDecision' | 'suggested' | 'mapped' | 'all'

/** What the bulk bar can do to the ticked rows. */
export type NominalBulkAction = 'exclude' | 'private' | 'activity' | 'clear'

/** "1 code", "12 codes": the count formatted, with the noun that agrees with it. */
export const pluralise = (count: number, one: string, many: string): string => `${count.toLocaleString()} ${count === 1 ? one : many}`

/** True for a code never seen on a line in the response's scope (a ledger account nothing posts to). */
export const hasNoNominalSpend = (row: INominalSummaryRow): boolean =>
  (Number(row.spend?.lineCount) || 0) === 0 && (Number(row.spend?.total) || 0) === 0

export const matchesNominalStatusFilter = (row: INominalSummaryRow, filter: NominalStatusFilter): boolean => {
  // Membership follows the saved state, not unsaved edits, so a row being worked on stays in view
  // until it is saved rather than vanishing the moment an outcome is chosen.
  if (filter === 'needsDecision') return !row.mapping
  if (filter === 'suggested') return !row.mapping && !!row.suggestion
  if (filter === 'mapped') return !!row.mapping
  return true
}

/**
 * Apply one row's change to the unsaved edits. An edit that only puts the saved mapping back is
 * dropped rather than kept, so the unsaved count never overstates what a save would change.
 * `suggestion` is left as it was unless the change says otherwise, so a row accepted from a
 * suggestion and then changed by hand is still saved as an overridden suggestion.
 */
export const applyNominalEdit = (
  prev: Record<string, INominalRowEdit>,
  key: string,
  row: INominalSummaryRow | undefined,
  next: { value: INominalOutcomeValue | null, conversion: ISpendBasedConversion | null, suggestion?: INominalRowEdit['suggestion'] },
): Record<string, INominalRowEdit> => {
  const existing = prev[key]
  const suggestion = next.suggestion !== undefined ? next.suggestion : (existing?.suggestion ?? null)
  if (row?.mapping && !suggestion && isSameOutcome(next.value, mappingToOutcomeValue(row.mapping))) {
    const remaining = { ...prev }
    delete remaining[key]
    return remaining
  }
  return {
    ...prev,
    [key]: {
      nominalCode: existing?.nominalCode ?? row?.mapping?.nominalCode ?? row?.nominalCode ?? key,
      // A saved description is kept as it is: the bulk save writes this field, so sending the
      // ledger's name instead would quietly overwrite a description someone typed.
      nominalDescription: existing?.nominalDescription ?? row?.mapping?.nominalDescription ?? row?.nominalDescription ?? null,
      mappingId: existing?.mappingId ?? row?.mapping?.id ?? null,
      value: next.value,
      conversion: next.value?.mappingType === 'spend' ? next.conversion : null,
      suggestion,
    },
  }
}

/** What a save of the current edits would send: upserts, deletions, and edits not ready to save. */
export interface INominalSavePlan {
  payload: IPostNominalCodeMapping[]
  // The edit key behind each payload item, in the same order.
  payloadKeys: string[]
  // Saved mappings that were cleared: deleted on save.
  deletions: { key: string, id: string }[]
  // Edited spend rows with no activity chosen yet. Not a decision, so they stay unsaved.
  incomplete: number
}

export const buildNominalSavePlan = (edits: Record<string, INominalRowEdit>): INominalSavePlan => {
  const plan: INominalSavePlan = { payload: [], payloadKeys: [], deletions: [], incomplete: 0 }
  Object.entries(edits).forEach(([key, edit]) => {
    if (!edit.value) {
      if (edit.mappingId) plan.deletions.push({ key, id: edit.mappingId })
      return
    }
    const item = buildNominalMappingPayloadItem(edit)
    if (!item) {
      plan.incomplete += 1
      return
    }
    plan.payload.push(item)
    plan.payloadKeys.push(key)
  })
  return plan
}
